/**
 * Customer → Billing & budget (threads/customer-billing-budget/mockup.html).
 *
 * "How much have we billed them, and how much is left?" on one screen, for any
 * customer: a plain-English headline, each budget against the calendar, the
 * year against last year, where the money went, what still needs a hand, and
 * every invoice — exportable in the columns the office already uses.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, FileText, MoreHorizontal, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { confirmDialog } from "@/components/ui/app-dialogs";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api, downloadAuthedFile } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PERMISSIONS } from "@/lib/permissions";
import { cn } from "@/lib/utils";
import { dateSortValue } from "@/lib/table-sort";
import { useTableControls } from "@/lib/use-table-controls";
import { SortHeaderButton } from "@/components/ui/sort-header-button";
import { BudgetDialog } from "./budget-dialog";
import { ContractDialog } from "./contract-dialog";
import { CumulativeChart, MonthlyChart } from "./charts";
import {
    budgetColor,
    budgetsForRange,
    calendarPct,
    CATEGORY_COLOR,
    CATEGORY_LABEL,
    headline,
    longDate,
    money,
    pct,
    shortCustomerName,
    shortDate,
    type BillingInvoice,
    type BillingResponse,
    type BudgetHealth,
    type BudgetStatus,
    type WorkCategory,
} from "./model";

const MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

function HealthPill({ health, children }: { health: BudgetHealth; children: React.ReactNode }) {
    const cls =
        health === "good"
            ? "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300"
            : health === "warn"
              ? "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300"
              : "border-red-300 bg-red-50 text-red-800 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300";
    const icon = health === "good" ? "✓" : health === "warn" ? "▲" : "✕";
    return (
        <span className={cn("inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-bold", cls)}>
            <span aria-hidden>{icon}</span>
            {children}
        </span>
    );
}

function Tile({ label, value, sub }: { label: string; value: string; sub?: string }) {
    return (
        <div className="rounded-xl border bg-background px-3.5 py-3">
            <div className="text-xs font-semibold text-muted-foreground">{label}</div>
            <div className="text-[22px] font-extrabold tabular-nums tracking-tight">{value}</div>
            {sub && <div className="mt-0.5 text-[11.5px] text-muted-foreground">{sub}</div>}
        </div>
    );
}

function hatch(color: string) {
    return `repeating-linear-gradient(135deg, ${color} 0 3px, transparent 3px 7px)`;
}

function BudgetCard({
    s,
    onEdit,
    onArchive,
    onRollForward,
    canManage,
}: {
    s: BudgetStatus;
    onEdit: () => void;
    onArchive: () => void;
    onRollForward: () => void;
    canManage: boolean;
}) {
    const b = s.budget;
    const color = budgetColor(b);
    const scale = Math.max(b.amountCents, s.usedCents + s.pendingCents);
    const w = (c: number) => `${(c / scale) * 100}%`;
    const capAt = (b.amountCents / scale) * 100;
    const todayAt = s.calendarPct * (capAt / 100);
    const counts =
        b.categories.length === 0 ? "everything billed" : b.categories.map((c) => CATEGORY_LABEL[c]).join(" + ");
    return (
        <div className="rounded-xl border bg-background p-4">
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
                    <h3 className="text-[15px] font-bold">{b.name}</h3>
                </div>
                <div className="flex items-center gap-1">
                    <HealthPill health={s.health}>
                        {s.health === "good" ? "On track" : s.health === "warn" ? "Watch" : "Over"}
                    </HealthPill>
                    {canManage && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    aria-label={`More for ${b.name}`}
                                >
                                    <MoreHorizontal className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onSelect={onEdit}>Edit budget</DropdownMenuItem>
                                <DropdownMenuItem onSelect={onRollForward}>
                                    Roll forward to next period
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={onArchive}>Retire budget</DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
            </div>
            <p className="mt-0.5 text-[12.5px] text-muted-foreground">
                <b className="tabular-nums text-foreground">{money(b.amountCents)}</b> · {longDate(b.periodStart)} –{" "}
                {longDate(b.periodEnd)} · counts {counts}
            </p>
            <div className="relative mb-1.5 mt-7 h-[18px] rounded-md border bg-muted/40">
                <div
                    className="absolute inset-y-0 left-0 rounded-l-md"
                    style={{ width: w(s.invoicedCents), background: color }}
                />
                <div
                    className="absolute inset-y-0"
                    style={{ left: w(s.invoicedCents), width: w(s.draftCents), background: hatch(color) }}
                />
                <div
                    className="absolute inset-y-0 opacity-60"
                    style={{ left: w(s.usedCents), width: w(s.pendingCents), background: hatch(color) }}
                />
                {capAt < 100 && (
                    <div className="absolute -inset-y-1.5 border-l-2 border-red-700" style={{ left: `${capAt}%` }} />
                )}
                <div
                    className="absolute -inset-y-1.5 border-l-2 border-dashed border-foreground"
                    style={{ left: `${todayAt}%` }}
                >
                    <span className="absolute -top-5 -left-3 whitespace-nowrap text-[10.5px] font-bold">today</span>
                </div>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>
                    <i
                        className="mr-1 inline-block h-2.5 w-2.5 rounded-sm align-[-1px]"
                        style={{ background: color }}
                    />
                    Invoiced {money(s.invoicedCents)}
                </span>
                {s.draftCents > 0 && (
                    <span>
                        <i
                            className="mr-1 inline-block h-2.5 w-2.5 rounded-sm border align-[-1px]"
                            style={{ background: hatch(color) }}
                        />
                        Drafted, not sent {money(s.draftCents)}
                    </span>
                )}
                {s.pendingCents > 0 && (
                    <span>
                        <i
                            className="mr-1 inline-block h-2.5 w-2.5 rounded-sm border align-[-1px] opacity-60"
                            style={{ background: hatch(color) }}
                        />
                        Quoted, waiting on them {money(s.pendingCents)} ({s.pendingCount})
                    </span>
                )}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Fig label="Used" value={pct(s.usedPct)} />
                <Fig label={s.remainingCents >= 0 ? "Left" : "Over by"} value={money(Math.abs(s.remainingCents))} />
                <Fig label="Period gone" value={pct(s.calendarPct)} />
                <Fig label="Projected" value={s.projectedCents === null ? "—" : money(s.projectedCents)} />
            </div>
            <div className="mt-3 rounded-lg border bg-muted/40 px-3 py-2 text-[13px]">
                {s.reason}
                {s.pendingCents > 0 && (
                    <>
                        {" "}
                        If every waiting quote is approved:{" "}
                        <b>{pct(((s.usedCents + s.pendingCents) / b.amountCents) * 100)}</b>.
                    </>
                )}
                {b.notes && <div className="mt-1 text-xs text-muted-foreground">📎 {b.notes}</div>}
            </div>
        </div>
    );
}

function Fig({ label, value }: { label: string; value: string }) {
    return (
        <div className="text-[11.5px] text-muted-foreground">
            {label}
            <b className="block text-[15px] tabular-nums text-foreground">{value}</b>
        </div>
    );
}

const STATUS_CLASS: Record<string, string> = {
    draft: "bg-muted text-foreground/70",
    open: "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300",
    partially_paid: "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300",
    paid: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300",
};
const STATUS_LABEL: Record<string, string> = {
    draft: "Draft",
    open: "Open",
    partially_paid: "Part paid",
    paid: "Paid",
};

const INVOICE_SORT: Record<string, (i: BillingInvoice) => string | number | null> = {
    date: (i) => dateSortValue(i.billedDate),
    number: (i) => i.invoiceNumber,
    wo: (i) => i.woNumber,
    location: (i) => i.siteName,
    workType: (i) => i.workType ?? CATEGORY_LABEL[i.category],
    total: (i) => i.totalCents,
    balance: (i) => i.balanceCents,
    status: (i) => STATUS_LABEL[i.status] ?? i.status,
};

export function BillingBudgetTab({ customerId }: { customerId: string }) {
    const { can } = useAuth();
    // Same gate as the server: see money AND manage the customer (Office+).
    const canManage = can(PERMISSIONS.finance.view) && can(PERMISSIONS.crm.manage);
    const qc = useQueryClient();
    const [range, setRange] = useState<{ start: string; end: string } | null>(null);
    const [customOpen, setCustomOpen] = useState(false);
    const [dialog, setDialog] = useState<{ editing: BudgetStatus | null } | null>(null);
    const [contractOpen, setContractOpen] = useState(false);
    const [catFilter, setCatFilter] = useState<WorkCategory | "all">("all");
    const [showAll, setShowAll] = useState(false);

    const q = useQuery({
        queryKey: ["customer-billing", customerId, range?.start ?? "", range?.end ?? ""],
        queryFn: () =>
            api.get<BillingResponse>(
                `/api/customers/${customerId}/billing${range ? `?from=${range.start}&to=${range.end}` : ""}`,
            ),
    });

    const setYearStart = useMutation({
        mutationFn: (startMonth: number) => api.patch(`/api/customers/${customerId}/fiscal-year`, { startMonth }),
        onSuccess: () => {
            setRange(null);
            qc.invalidateQueries({ queryKey: ["customer-billing", customerId] });
            toast.success("Year start saved.");
        },
    });

    const archive = useMutation({
        mutationFn: (id: string) => api.post(`/api/customer-budgets/${id}/archive`, {}),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["customer-billing", customerId] });
            toast.success("Budget retired — kept on file.");
        },
    });

    const portalShare = useMutation({
        mutationFn: (show: boolean) => api.patch(`/api/customers/${customerId}/portal-budget`, { show }),
        onSuccess: (_r, show) => {
            qc.invalidateQueries({ queryKey: ["customer-billing", customerId] });
            toast.success(
                show ? "Shared — their portal login now shows a Budget page." : "No longer shown in their portal.",
            );
        },
        onError: () => toast.error("Couldn't change that."),
    });

    const rollForward = useMutation({
        mutationFn: (id: string) => api.post(`/api/customer-budgets/${id}/roll-forward`, {}),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["customer-billing", customerId] });
            toast.success("Rolled forward — the new period is set up with the same amount and people.");
        },
        onError: () => toast.error("Couldn't roll it forward."),
    });

    const d = q.data;
    const invoices = useMemo(
        () => (d?.invoices ?? []).filter((i) => catFilter === "all" || i.category === catFilter),
        [d, catFilter],
    );
    // Sorted before the 25-row cut, so "Total, biggest first" means the
    // biggest of the period, not the biggest of the first 25.
    const {
        visible: sortedInvoices,
        sortKey,
        sortDir,
        toggleSort,
    } = useTableControls(invoices, {
        sortAccessors: INVOICE_SORT,
        sortId: "customer.billing-invoices",
        descFirst: ["date", "total", "balance"],
    });
    const sortTh = (key: string, label: string) => (
        <SortHeaderButton
            label={label}
            active={sortKey === key}
            dir={sortDir}
            onClick={() => toggleSort(key)}
            className="uppercase tracking-wide"
        />
    );

    if (q.isLoading)
        return (
            <Card>
                <CardContent className="py-10 text-center text-sm text-muted-foreground">Adding it all up…</CardContent>
            </Card>
        );
    if (q.isError || !d)
        return (
            <Card>
                <CardContent className="py-10 text-center text-sm text-destructive">
                    Couldn't load billing for this customer.
                </CardContent>
            </Card>
        );

    const who = shortCustomerName(d.customer.name);
    const t = d.summary.totals;
    const h = headline(d, who);
    const running = d.today >= d.range.start && d.today <= d.range.end;
    const monthsIdx = d.summary.months.findIndex((m) => d.today.startsWith(m.month));
    const spanning = budgetsForRange(d.budgets, d.range);
    const capCents = spanning.length > 0 ? spanning.reduce((a, b) => a + b.budget.amountCents, 0) : null;
    const usedCents = spanning.reduce((a, b) => a + b.usedCents, 0);
    const activePreset = d.presets.find((p) => p.start === d.range.start && p.end === d.range.end)?.key;
    const maxSite = Math.max(1, ...d.summary.topSites.map((s) => s.cents));
    const pendingTotal = d.pending.reduce((a, p) => a + p.totalCents, 0);
    const drafts = d.invoices.filter((i) => i.status === "draft");
    const notes = d.summary.dataNotes;
    const visibleInvoices = showAll ? sortedInvoices : sortedInvoices.slice(0, 25);

    const exportExcel = () =>
        downloadAuthedFile(
            `/api/customers/${customerId}/billing.xlsx?from=${d.range.start}&to=${d.range.end}`,
            `${who} invoices.xlsx`,
        ).catch(() => toast.error("Export failed"));

    const confirmArchive = async (s: BudgetStatus) => {
        if (
            await confirmDialog({
                title: `Retire “${s.budget.name}”?`,
                message: "It stops showing here and stops sending heads-ups. It's kept on file.",
                confirmLabel: "Retire",
            })
        )
            archive.mutate(s.budget.id);
    };

    return (
        <div className="space-y-4">
            {/* ── Toolbar ─────────────────────────────── */}
            <Card>
                <CardContent className="space-y-2 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-1.5">
                            {d.presets.map((p) => (
                                <button
                                    key={p.key}
                                    type="button"
                                    onClick={() => {
                                        setRange({ start: p.start, end: p.end });
                                        setCustomOpen(false);
                                    }}
                                    className={cn(
                                        "rounded-full border px-3 py-1 text-[12.5px] font-semibold",
                                        activePreset === p.key && !customOpen
                                            ? "border-foreground bg-foreground text-background"
                                            : "bg-background text-muted-foreground hover:bg-muted",
                                    )}
                                >
                                    {p.label}
                                </button>
                            ))}
                            <button
                                type="button"
                                onClick={() => setCustomOpen(!customOpen)}
                                className={cn(
                                    "rounded-full border px-3 py-1 text-[12.5px] font-semibold",
                                    customOpen || !activePreset
                                        ? "border-foreground bg-foreground text-background"
                                        : "bg-background text-muted-foreground hover:bg-muted",
                                )}
                            >
                                Custom range…
                            </button>
                            {customOpen && (
                                <span className="flex items-center gap-1.5">
                                    <Input
                                        type="date"
                                        defaultValue={d.range.start}
                                        className="h-8 w-[150px]"
                                        aria-label="From"
                                        onBlur={(e) =>
                                            e.target.value &&
                                            setRange({ start: e.target.value, end: range?.end ?? d.range.end })
                                        }
                                    />
                                    <span className="text-muted-foreground">to</span>
                                    <Input
                                        type="date"
                                        defaultValue={d.range.end}
                                        className="h-8 w-[150px]"
                                        aria-label="To"
                                        onBlur={(e) =>
                                            e.target.value &&
                                            setRange({ start: range?.start ?? d.range.start, end: e.target.value })
                                        }
                                    />
                                </span>
                            )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Button size="sm" variant="outline" onClick={() => void exportExcel()}>
                                <Download className="mr-1 h-3.5 w-3.5" /> Excel
                            </Button>
                            {canManage && (
                                <Button size="sm" variant="outline" onClick={() => setContractOpen(true)}>
                                    <FileText className="mr-1 h-3.5 w-3.5" /> From a contract
                                </Button>
                            )}
                            {canManage && (
                                <Button size="sm" onClick={() => setDialog({ editing: null })}>
                                    <Plus className="mr-1 h-3.5 w-3.5" /> Set a budget
                                </Button>
                            )}
                        </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        {shortDate(d.range.start)}, {d.range.start.slice(0, 4)} – {shortDate(d.range.end)},{" "}
                        {d.range.end.slice(0, 4)}
                        {running && (
                            <> · today is {pct(calendarPct(d.range.start, d.range.end, d.today))} of the way through</>
                        )}
                        {" · "}this customer's year starts in{" "}
                        {canManage ? (
                            <select
                                className="rounded border bg-background px-1 py-0.5 text-xs font-semibold text-foreground"
                                value={d.customer.fiscalYearStartMonth}
                                onChange={(e) => setYearStart.mutate(Number(e.target.value))}
                                aria-label="Year starts in"
                            >
                                {MONTH_NAMES.map((m, i) => (
                                    <option key={m} value={i + 1}>
                                        {m}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <b>{MONTH_NAMES[d.customer.fiscalYearStartMonth - 1]}</b>
                        )}
                    </p>
                </CardContent>
            </Card>

            {/* ── Hero ────────────────────────────────── */}
            <Card>
                <CardContent className="grid gap-5 py-5 lg:grid-cols-[1.25fr_1fr] lg:items-center">
                    <div>
                        {h.health && (
                            <HealthPill health={h.health}>
                                {h.health === "good"
                                    ? "On track"
                                    : h.health === "warn"
                                      ? "Worth a look"
                                      : "Over budget"}
                            </HealthPill>
                        )}
                        <div className="mb-2 mt-1.5 text-[24px] font-extrabold leading-tight tracking-tight">
                            {h.text}
                        </div>
                        {running && capCents !== null && d.summary.projectedCents !== null && (
                            <p className="text-sm text-muted-foreground">
                                At last year's rhythm it finishes around{" "}
                                <b className="text-foreground">{money(d.summary.projectedCents)}</b>
                                {d.summary.projectedCents <= capCents ? (
                                    <>
                                        , about{" "}
                                        <b className="text-foreground">
                                            {money(capCents - d.summary.projectedCents)} under
                                        </b>{" "}
                                        the budget.
                                    </>
                                ) : (
                                    <>
                                        , about{" "}
                                        <b className="text-foreground">
                                            {money(d.summary.projectedCents - capCents)} over
                                        </b>{" "}
                                        the budget.
                                    </>
                                )}
                            </p>
                        )}
                        {running && capCents !== null && (
                            <div className="relative mt-6 space-y-5">
                                <div className="relative h-3.5 rounded-full border bg-muted/50">
                                    <div
                                        className="absolute inset-y-0 left-0 rounded-full bg-muted-foreground/30"
                                        style={{ width: `${calendarPct(d.range.start, d.range.end, d.today)}%` }}
                                    />
                                    <span
                                        className="absolute -top-5 text-[11px] font-bold"
                                        style={{
                                            left: `${calendarPct(d.range.start, d.range.end, d.today)}%`,
                                            transform: "translateX(-50%)",
                                        }}
                                    >
                                        📅 year {pct(calendarPct(d.range.start, d.range.end, d.today))}
                                    </span>
                                </div>
                                <div className="relative h-3.5 rounded-full border bg-muted/50">
                                    <div
                                        className="absolute inset-y-0 left-0 rounded-full"
                                        style={{
                                            width: `${Math.min(100, (usedCents / capCents) * 100)}%`,
                                            background: "#2a78d6",
                                        }}
                                    />
                                    <span
                                        className="absolute -top-5 text-[11px] font-bold text-[#2a78d6]"
                                        style={{
                                            left: `${Math.min(100, (usedCents / capCents) * 100)}%`,
                                            transform: "translateX(-50%)",
                                        }}
                                    >
                                        💵 budget {pct((usedCents / capCents) * 100)}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-2.5">
                        <Tile
                            label={running ? "Billed so far" : "Billed"}
                            value={money(t.billedCents)}
                            sub={`${t.invoiceCount} invoices${t.draftCount ? ` · ${t.draftCount} still drafts` : ""}`}
                        />
                        {capCents !== null ? (
                            <Tile
                                label="Budget left"
                                value={money(capCents - usedCents)}
                                sub={`of ${money(capCents)}`}
                            />
                        ) : (
                            <Tile
                                label="Same point last year"
                                value={money(t.lastYearSameDateCents)}
                                sub={`${money(t.lastYearTotalCents)} for the whole year`}
                            />
                        )}
                        <Tile
                            label="They still owe"
                            value={money(t.owedCents)}
                            sub={`${t.owedCount} open${t.overdueCount ? ` · ${t.overdueCount} overdue` : " · nothing overdue"}`}
                        />
                        <Tile
                            label="Waiting on their OK"
                            value={money(pendingTotal)}
                            sub={`${d.pending.length} quote${d.pending.length === 1 ? "" : "s"} · not counted as spent`}
                        />
                    </div>
                </CardContent>
            </Card>

            {/* ── Budgets ─────────────────────────────── */}
            {d.budgets.length > 0 ? (
                <div className="grid gap-4 lg:grid-cols-2">
                    {d.budgets.map((s) => (
                        <BudgetCard
                            key={s.budget.id}
                            s={s}
                            canManage={canManage}
                            onEdit={() => setDialog({ editing: s })}
                            onArchive={() => void confirmArchive(s)}
                            onRollForward={() => rollForward.mutate(s.budget.id)}
                        />
                    ))}
                </div>
            ) : null}
            {d.budgets.length > 0 && canManage && (
                <label className="flex w-fit cursor-pointer items-center gap-2 rounded-lg border bg-background px-3 py-2 text-sm">
                    <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={!!d.customer.portalShowBudget}
                        disabled={portalShare.isPending}
                        onChange={(e) => portalShare.mutate(e.target.checked)}
                        data-testid="portal-budget-toggle"
                    />
                    <span>
                        Show these budgets in {who}'s customer portal
                        <span className="block text-xs text-muted-foreground">
                            Read-only: billed so far against each cap, and the quotes waiting on them. Drafts never
                            show.
                        </span>
                    </span>
                </label>
            )}
            {d.budgets.length > 0 ? null : (
                <Card>
                    <CardContent className="flex flex-wrap items-center justify-between gap-2 py-4 text-sm">
                        <span className="text-muted-foreground">
                            No budget set for this customer in this period. Everything below still works.
                        </span>
                        {canManage && (
                            <span className="flex gap-2">
                                <Button size="sm" onClick={() => setContractOpen(true)}>
                                    <FileText className="mr-1 h-3.5 w-3.5" /> Set up from a contract
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setDialog({ editing: null })}>
                                    <Plus className="mr-1 h-3.5 w-3.5" /> Set a budget
                                </Button>
                            </span>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* ── Charts ──────────────────────────────── */}
            {t.invoiceCount === 0 && t.lastYearTotalCents === 0 ? (
                <Card>
                    <CardContent className="py-8 text-center text-sm text-muted-foreground">
                        Nothing billed to this customer in this period, or the same one last year.
                    </CardContent>
                </Card>
            ) : (
                <div className="grid gap-4 xl:grid-cols-2">
                    <Card>
                        <CardContent className="py-4">
                            <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                                <div>
                                    <div className="text-[11px] font-extrabold uppercase tracking-wider text-muted-foreground">
                                        Through the period
                                    </div>
                                    <div className="text-base font-bold">Running total, this year against last</div>
                                </div>
                                <Legend
                                    items={[
                                        ["This year", "#2a78d6", "solid"],
                                        ["Projected", "#2a78d6", "dot"],
                                        ["Last year", "#9b958b", "dash"],
                                    ]}
                                />
                            </div>
                            <CumulativeChart
                                months={d.summary.months}
                                todayIndex={running ? monthsIdx : -1}
                                projectedCents={d.summary.projectedCents}
                                budgetCents={capCents}
                            />
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="py-4">
                            <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                                <div>
                                    <div className="text-[11px] font-extrabold uppercase tracking-wider text-muted-foreground">
                                        Month by month
                                    </div>
                                    <div className="text-base font-bold">What was billed each month</div>
                                </div>
                                <Legend
                                    items={[
                                        ["This year", "#2a78d6", "solid"],
                                        ["Last year", "#cfc8bc", "solid"],
                                    ]}
                                />
                            </div>
                            <MonthlyChart months={d.summary.months} />
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* ── Where it went / next steps ──────────── */}
            <div className="grid gap-4 lg:grid-cols-2">
                <Card>
                    <CardContent className="py-4">
                        <div className="text-[11px] font-extrabold uppercase tracking-wider text-muted-foreground">
                            Where it went
                        </div>
                        <div className="mb-2 text-base font-bold">Top locations</div>
                        {d.summary.topSites.length === 0 && (
                            <p className="text-sm text-muted-foreground">Nothing billed in this period.</p>
                        )}
                        {d.summary.topSites.map((s) => (
                            <div
                                key={s.siteName}
                                className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_84px] items-center gap-2.5 py-1 text-[13px]"
                            >
                                <span className="truncate" title={s.siteName}>
                                    {s.siteName}
                                </span>
                                <div
                                    className="h-2.5 rounded-r"
                                    style={{
                                        width: `${(s.cents / maxSite) * 100}%`,
                                        background: CATEGORY_COLOR[s.category],
                                    }}
                                />
                                <span className="text-right font-bold tabular-nums">{money(s.cents)}</span>
                            </div>
                        ))}
                        {d.summary.byCategory.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t pt-2 text-xs text-muted-foreground">
                                {d.summary.byCategory.map((c) => (
                                    <span key={c.category}>
                                        <i
                                            className="mr-1 inline-block h-2.5 w-2.5 rounded-sm align-[-1px]"
                                            style={{ background: CATEGORY_COLOR[c.category] }}
                                        />
                                        {CATEGORY_LABEL[c.category]} {money(c.cents)} ({c.count})
                                    </span>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="space-y-2.5 py-4">
                        <div>
                            <div className="text-[11px] font-extrabold uppercase tracking-wider text-muted-foreground">
                                Money &amp; next steps
                            </div>
                            <div className="text-base font-bold">What still needs a hand</div>
                        </div>
                        {drafts.length > 0 && (
                            <Note tone="warn">
                                ✉️{" "}
                                <b>
                                    {drafts.length} invoice{drafts.length === 1 ? " is" : "s are"} still drafts (
                                    {money(drafts.reduce((a, i) => a + i.totalCents, 0))}).
                                </b>{" "}
                                They count against the budget, but the customer hasn't seen them:{" "}
                                {drafts.slice(0, 6).map((i, n) => (
                                    <span key={i.id}>
                                        {n > 0 && ", "}
                                        <Link className="underline" to={`/invoices/${i.id}`}>
                                            {i.siteName ?? i.invoiceNumber ?? "invoice"}
                                        </Link>
                                    </span>
                                ))}
                                {drafts.length > 6 && ` and ${drafts.length - 6} more`}.
                            </Note>
                        )}
                        {d.pending.length > 0 && (
                            <Note>
                                💬{" "}
                                <b>
                                    {d.pending.length} quote{d.pending.length === 1 ? " is" : "s are"} waiting on them (
                                    {money(pendingTotal)}):
                                </b>{" "}
                                {d.pending.slice(0, 4).map((p, n) => (
                                    <span key={p.id}>
                                        {n > 0 && " · "}
                                        <Link className="underline" to={`/commercial/${p.id}`}>
                                            {p.siteName ?? p.number ?? "quote"}
                                        </Link>{" "}
                                        {money(p.totalCents)}
                                    </span>
                                ))}
                                {d.pending.length > 4 && ` · ${d.pending.length - 4} more`}
                            </Note>
                        )}
                        {d.approvedUnbilled.length === 0 ? (
                            <Note tone="good">
                                <b className="text-emerald-700 dark:text-emerald-300">
                                    ✓ Nothing approved is sitting unbilled.
                                </b>
                            </Note>
                        ) : (
                            <Note tone="warn">
                                🧾{" "}
                                <b>
                                    {d.approvedUnbilled.length} approved quote
                                    {d.approvedUnbilled.length === 1 ? " hasn't" : "s haven't"} been billed yet
                                </b>{" "}
                                ({money(d.approvedUnbilled.reduce((a, e) => a + e.totalCents, 0))}):{" "}
                                {d.approvedUnbilled.slice(0, 4).map((e, n) => (
                                    <span key={e.id}>
                                        {n > 0 && " · "}
                                        <Link className="underline" to={`/commercial/${e.id}`}>
                                            {e.siteName ?? e.number ?? "quote"}
                                        </Link>
                                    </span>
                                ))}
                            </Note>
                        )}
                        <Note>
                            ⏳ <b>{money(t.owedCents)} owed</b> across {t.owedCount} open invoice
                            {t.owedCount === 1 ? "" : "s"}
                            {t.overdueCount > 0 ? (
                                <>
                                    {" "}
                                    —{" "}
                                    <b>
                                        {t.overdueCount} overdue ({money(t.overdueCents)})
                                    </b>
                                    .
                                </>
                            ) : (
                                ", nothing overdue."
                            )}
                        </Note>
                    </CardContent>
                </Card>
            </div>

            {/* ── Every invoice ───────────────────────── */}
            <Card>
                <CardContent className="py-4">
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <div>
                            <div className="text-[11px] font-extrabold uppercase tracking-wider text-muted-foreground">
                                Every invoice in the period
                            </div>
                            <div className="text-base font-bold">
                                {invoices.length} invoice{invoices.length === 1 ? "" : "s"} ·{" "}
                                {money(invoices.reduce((a, i) => a + i.totalCents, 0))}
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5">
                            {(
                                ["all", ...d.summary.byCategory.map((c) => c.category)] as Array<WorkCategory | "all">
                            ).map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => setCatFilter(c)}
                                    className={cn(
                                        "rounded-full border px-2.5 py-0.5 text-xs font-semibold",
                                        catFilter === c
                                            ? "border-foreground bg-foreground text-background"
                                            : "text-muted-foreground hover:bg-muted",
                                    )}
                                >
                                    {c === "all" ? "All" : CATEGORY_LABEL[c]}
                                </button>
                            ))}
                            <Button size="sm" variant="outline" onClick={() => void exportExcel()}>
                                <Download className="mr-1 h-3.5 w-3.5" /> Excel
                            </Button>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-[12.5px]">
                            <thead>
                                <tr className="border-b bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                                    <th className="px-2 py-1.5">{sortTh("date", "Date")}</th>
                                    <th className="px-2 py-1.5">{sortTh("number", "Invoice #")}</th>
                                    <th className="px-2 py-1.5">{sortTh("wo", "WO / job")}</th>
                                    <th className="px-2 py-1.5">{sortTh("location", "Location")}</th>
                                    <th className="px-2 py-1.5">{sortTh("workType", "Work type")}</th>
                                    <th className="px-2 py-1.5 text-right">{sortTh("total", "Total")}</th>
                                    <th className="px-2 py-1.5 text-right">{sortTh("balance", "Balance")}</th>
                                    <th className="px-2 py-1.5">{sortTh("status", "Status")}</th>
                                </tr>
                            </thead>
                            <tbody className="tabular-nums">
                                {visibleInvoices.map((i) => (
                                    <tr key={i.id} className="border-b last:border-0 hover:bg-muted/30">
                                        <td
                                            className="px-2 py-1.5 whitespace-nowrap"
                                            title={
                                                i.dateSource === "job"
                                                    ? "Imported from ServiceTitan — dated by its job's completion"
                                                    : undefined
                                            }
                                        >
                                            {shortDate(i.billedDate)}
                                            {i.dateSource !== "invoice" && i.imported && (
                                                <span className="text-muted-foreground">*</span>
                                            )}
                                        </td>
                                        <td className="px-2 py-1.5">
                                            <Link className="hover:underline" to={`/invoices/${i.id}`}>
                                                {i.invoiceNumber ?? "—"}
                                            </Link>
                                        </td>
                                        <td className="px-2 py-1.5">
                                            {i.jobId ? (
                                                <Link className="hover:underline" to={`/jobs/${i.jobId}`}>
                                                    {i.woNumber ?? "job"}
                                                </Link>
                                            ) : (
                                                "—"
                                            )}
                                        </td>
                                        <td
                                            className="max-w-[220px] truncate px-2 py-1.5"
                                            title={i.siteName ?? undefined}
                                        >
                                            {i.siteName ?? "—"}
                                        </td>
                                        <td className="px-2 py-1.5">
                                            <span className="inline-flex items-center gap-1.5">
                                                <i
                                                    className="inline-block h-2 w-2 rounded-sm"
                                                    style={{ background: CATEGORY_COLOR[i.category] }}
                                                />
                                                {i.workType ?? CATEGORY_LABEL[i.category]}
                                            </span>
                                        </td>
                                        <td className="px-2 py-1.5 text-right">
                                            {money(i.totalCents, { exact: true })}
                                        </td>
                                        <td className="px-2 py-1.5 text-right">
                                            {money(i.balanceCents, { exact: true })}
                                        </td>
                                        <td className="px-2 py-1.5">
                                            <span
                                                className={cn(
                                                    "rounded px-1.5 py-0.5 text-[11px] font-bold",
                                                    STATUS_CLASS[i.status] ?? "bg-muted",
                                                )}
                                            >
                                                {STATUS_LABEL[i.status] ?? i.status}
                                            </span>
                                            {/* How late, on the row, so nobody opens each overdue
                                                invoice to find out. Unpaid only, the same rule as the
                                                customer's Invoices tab; a month or more reads red. */}
                                            {(i.status === "open" || i.status === "partially_paid") &&
                                                i.balanceCents > 0 &&
                                                (i.daysLate ?? 0) > 0 && (
                                                    <span
                                                        className={cn(
                                                            "ml-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-bold",
                                                            (i.daysLate ?? 0) >= 30
                                                                ? "bg-destructive text-destructive-foreground"
                                                                : "bg-amber-300 text-amber-950",
                                                        )}
                                                    >
                                                        {i.daysLate} {i.daysLate === 1 ? "day" : "days"} late
                                                    </span>
                                                )}
                                        </td>
                                    </tr>
                                ))}
                                {invoices.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className="px-2 py-6 text-center text-muted-foreground">
                                            No invoices in this period.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {invoices.length > 25 && (
                        // Says up front how many "Show all" will add.
                        <div className="mt-2 flex items-center justify-between gap-3 text-xs">
                            <span className="text-muted-foreground">
                                {showAll
                                    ? `Showing all ${invoices.length} invoices`
                                    : `Showing 25 of ${invoices.length} invoices`}
                            </span>
                            <button
                                type="button"
                                className="font-semibold text-primary underline"
                                onClick={() => setShowAll(!showAll)}
                            >
                                {showAll ? "Show fewer" : `Show all ${invoices.length}`}
                            </button>
                        </div>
                    )}
                    {(notes.datedFromJob > 0 || notes.datedFromImport > 0 || notes.uncategorized > 0) && (
                        <ul className="mt-3 list-disc space-y-0.5 border-t pl-5 pt-2 text-xs text-muted-foreground">
                            {notes.datedFromJob > 0 && (
                                <li>
                                    * {notes.datedFromJob} invoice{notes.datedFromJob === 1 ? " was" : "s were"}{" "}
                                    imported from ServiceTitan without their invoice date, so{" "}
                                    {notes.datedFromJob === 1 ? "it's" : "they're"} dated by the job's completion.
                                </li>
                            )}
                            {notes.datedFromImport > 0 && (
                                <li>
                                    {notes.datedFromImport} imported invoice
                                    {notes.datedFromImport === 1 ? " has" : "s have"} no invoice or job date, so{" "}
                                    {notes.datedFromImport === 1 ? "it's" : "they're"} dated by the import day.
                                </li>
                            )}
                            {notes.uncategorized > 0 && (
                                <li>
                                    {notes.uncategorized} invoice{notes.uncategorized === 1 ? " has" : "s have"} a job
                                    type that isn't testing, repair or replacement, so{" "}
                                    {notes.uncategorized === 1 ? "it counts" : "they count"} only toward budgets that
                                    cover everything or “Other”.
                                </li>
                            )}
                        </ul>
                    )}
                </CardContent>
            </Card>

            {contractOpen && <ContractDialog open onClose={() => setContractOpen(false)} data={d} />}
            {dialog && (
                <BudgetDialog
                    open
                    onClose={() => setDialog(null)}
                    data={d}
                    editing={
                        dialog.editing ? { ...dialog.editing.budget, alertUserIds: dialog.editing.alertUserIds } : null
                    }
                />
            )}
        </div>
    );
}

function Note({ tone, children }: { tone?: "warn" | "good"; children: React.ReactNode }) {
    return (
        <div
            className={cn(
                "rounded-lg border px-3 py-2 text-[13px]",
                tone === "warn"
                    ? "border-amber-300/70 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10"
                    : tone === "good"
                      ? "border-emerald-300/70 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10"
                      : "bg-muted/40",
            )}
        >
            {children}
        </div>
    );
}

function Legend({ items }: { items: Array<[string, string, "solid" | "dot" | "dash"]> }) {
    return (
        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
            {items.map(([label, color, kind]) => (
                <span key={label} className="inline-flex items-center gap-1.5">
                    {kind === "solid" ? (
                        <i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
                    ) : (
                        <i
                            className="inline-block w-4"
                            style={{ borderTop: `2px ${kind === "dot" ? "dotted" : "dashed"} ${color}` }}
                        />
                    )}
                    {label}
                </span>
            ))}
        </div>
    );
}
